import React, { useState, useEffect, useRef } from "react";
import API from "../api";
import { Loading } from "../components/UI";

export default function BotTester() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [phone, setPhone] = useState("919876543210");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientsLoading, setClientsLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    API.get("/clients").then(r => {
      const active = r.data.filter(c => c.isActive);
      setClients(active);
      if (active.length > 0) setSelectedClient(active[0]._id);
    }).finally(() => setClientsLoading(false));
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || !selectedClient) return;
    setMessages(p => [...p, { role: "user", text, time: new Date() }]);
    setInput("");
    setLoading(true);
    try {
      const res = await API.post("/webhook/simulate", { clientId: selectedClient, phone, message: text });
      setMessages(p => [...p, { role: "bot", text: res.data.botReply, time: new Date() }]);
    } catch (err) {
      setMessages(p => [...p, { role: "bot", text: `⚠️ Error: ${err.response?.data?.error || err.message}`, time: new Date(), isError: true }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    if (!selectedClient) return;
    await API.post("/webhook/reset", { clientId: selectedClient, phone }).catch(console.error);
    setMessages([]);
  };

  if (clientsLoading) return <Loading />;

  const sel = { background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "8px 10px", color: "var(--text-primary)", fontSize: 12, fontFamily: "var(--font-body)", outline: "none", cursor: "pointer" };

  return (
    <div className="fade-in" style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 20, height: "calc(100vh - 130px)" }}>

      {/* Config Panel */}
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Bot Tester</div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>Simulate WhatsApp conversations. Leads save automatically.</p>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Client</label>
          <select value={selectedClient} onChange={e => { setSelectedClient(e.target.value); setMessages([]); }} style={sel}>
            {clients.length === 0 ? <option>No active clients</option> : clients.map(c => <option key={c._id} value={c._id}>{c.name} ({c.brand})</option>)}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 600, textTransform: "uppercase" }}>Test Phone</label>
          <input value={phone} onChange={e => setPhone(e.target.value)} style={{ ...sel, fontFamily: "var(--font-mono)" }} />
        </div>

        <button onClick={reset} style={{ background: "transparent", border: "1px solid var(--border-light)", borderRadius: "var(--radius)", padding: 8, color: "var(--text-secondary)", fontSize: 12, cursor: "pointer", fontFamily: "var(--font-body)" }}>↺ Reset Conversation</button>

        <div style={{ height: 1, background: "var(--border)" }} />
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Quick Replies</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {["Hi", "1", "2", "Splendor Plus", "Rahul - Splendor Plus", "1"].map((r, i) => (
            <button key={i} onClick={() => sendMessage(r)} disabled={!selectedClient || loading}
              style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "7px 8px", color: "var(--text-secondary)", fontSize: 12, cursor: "pointer", fontFamily: "var(--font-mono)" }}>
              {r}
            </button>
          ))}
        </div>

        <div style={{ height: 1, background: "var(--border)" }} />
        <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 12 }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", marginBottom: 8 }}>Flow</div>
          {["1. Send \"Hi\" to start", "2. Reply 1 or 2", "3. Bike → type model", "4. Service → Name - Model"].map((s, i) => (
            <div key={i} style={{ fontSize: 11, color: "var(--text-muted)", borderLeft: "2px solid var(--accent)", paddingLeft: 8, marginBottom: 4 }}>{s}</div>
          ))}
        </div>
      </div>

      {/* Chat Panel */}
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", background: "var(--bg-elevated)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, background: "var(--accent-glow)", border: "1px solid rgba(37,211,102,0.3)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>🤖</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{clients.find(c => c._id === selectedClient)?.name || "Select a client"}</div>
              <div style={{ fontSize: 11, color: "var(--accent)", display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ display: "inline-block", width: 6, height: 6, background: "var(--accent)", borderRadius: "50%", animation: "pulse 2s infinite" }} />
                WhatsApp Bot — Online
              </div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>{phone}</div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column" }}>
          {messages.length === 0 && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", textAlign: "center", paddingTop: 60 }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>💬</div>
              <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>Send a message to start</div>
              <div style={{ fontSize: 11, marginTop: 6 }}>Try "Hi" or click a Quick Reply</div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", marginBottom: 10, animation: "fadeIn 0.2s ease" }}>
              <div style={{
                maxWidth: "72%", padding: "10px 14px",
                borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                background: msg.role === "user" ? "var(--accent)" : (msg.isError ? "rgba(255,77,77,0.1)" : "var(--bg-elevated)"),
                color: msg.role === "user" ? "#000" : (msg.isError ? "var(--accent-red)" : "var(--text-primary)"),
                border: msg.role === "bot" ? "1px solid var(--border)" : "none",
                fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word"
              }}>
                {msg.text}
                <div style={{ fontSize: 10, marginTop: 4, opacity: 0.6, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                  {msg.time.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", gap: 6, padding: "8px 0 8px 4px" }}>
              {[0, 1, 2].map(i => <div key={i} style={{ width: 7, height: 7, background: "var(--text-muted)", borderRadius: "50%", animation: `pulse 1.2s ease ${i * 0.2}s infinite` }} />)}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 10, alignItems: "center", background: "var(--bg-elevated)" }}>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
            placeholder="Type a message... (Enter to send)"
            disabled={!selectedClient || loading}
            style={{ flex: 1, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 20, padding: "10px 16px", color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)", outline: "none" }} />
          <button onClick={() => sendMessage(input)} disabled={!input.trim() || !selectedClient || loading}
            style={{ width: 38, height: 38, background: "var(--accent)", border: "none", borderRadius: "50%", cursor: "pointer", fontSize: 14, color: "#000", fontWeight: 700 }}>➤</button>
        </div>
      </div>
    </div>
  );
}

